import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, ChevronDown, Mail, Phone, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Proposal, deleteProposal, getProposal } from '../lib/proposals';
import { useProposals } from '../hooks/useProposals';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from '../components/EmptyState';
import CreateProposalModal from '../components/CreateProposalModal';
import ProposalDetailsModal from '../components/ProposalDetailsModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import PermissionGuard from '../components/PermissionGuard';

const statusOptions = [
  { label: 'All Proposals', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Viewed', value: 'viewed' },
  { label: 'Approved', value: 'approved' },
  { label: 'Declined', value: 'declined' }
];

interface ProposalsProps {
  readOnly?: boolean;
}

function Proposals({ readOnly = false }: ProposalsProps) {
  const { selectedBusiness } = useBusiness();
  const location = useLocation();
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const { proposals, loading, loadingMore, error, hasMore, totalCount, refetch, loadMore } = useProposals(selectedBusiness?.id);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<Proposal | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const filteredProposals = proposals.filter((proposal) => {
    if (selectedStatus.value !== 'all' && proposal.status !== selectedStatus.value) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      proposal.title.toLowerCase().includes(searchLower) ||
      proposal.customer?.first_name?.toLowerCase().includes(searchLower) ||
      proposal.customer?.last_name?.toLowerCase().includes(searchLower) ||
      proposal.customer?.email?.toLowerCase().includes(searchLower)
    );
  });

  // Handle navigation from customer details modal with proposal ID
  useEffect(() => {
    const handleProposalNavigation = async () => {
      const state = location.state as { selectedProposalId?: string } | null;
      if (state?.selectedProposalId && selectedBusiness?.id) {
        try {
          // Fetch the specific proposal details
          const proposal = await getProposal(state.selectedProposalId);
          setSelectedProposal(proposal);
          setIsDetailsModalOpen(true);
          
          // Clear the navigation state to prevent reopening on refresh
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error fetching proposal from navigation:', err);
        }
      }
    };

    handleProposalNavigation();
  }, [location.state, selectedBusiness?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      case 'viewed':
        return 'bg-purple-100 text-purple-700';
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'declined':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Proposals</h1>
          {totalCount > 0 && (
            <p className="text-sm text-gray-500">
              Showing {proposals.length} of {totalCount} proposals
            </p>
          )}
        </div>
        {!readOnly && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Proposal
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5]">
        <div className="px-4 py-3 border-b border-[#e1e3e5] flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-stretch sm:items-center">
          <div className="relative">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 focus:outline-none"
            >
              {selectedStatus.label}
              <ChevronDown
                className={`ml-1.5 h-4 w-4 transition-transform duration-200 ${
                  isStatusOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isStatusOpen && (
              <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setIsStatusOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm ${
                        selectedStatus.value === option.value
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      role="menuitem"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search proposals..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-9 pr-4 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Mobile List View */}
        <div className="sm:hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading proposals...</p>
              </div>
            </div>
          ) : filteredProposals.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<FileText className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching proposals' : 'No proposals yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start creating proposals to send to your customers.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Create Proposal'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateModalOpen(true))}
              />
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredProposals.map((proposal) => (
                <div 
                  key={proposal.id}
                  className="p-4 hover:bg-gray-50"
                  onClick={() => {
                    setSelectedProposal(proposal);
                    setIsDetailsModalOpen(true);
                  }}
                >
                  <div className="flex items-start space-x-3">
                    <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white">
                      {proposal.customer?.first_name?.[0]}{proposal.customer?.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {proposal.customer?.first_name} {proposal.customer?.last_name}
                        </div>
                        <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(proposal.status)}`}>
                          {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{proposal.title}</div>
                      <div className="mt-2 text-sm font-medium text-gray-900">
                        ${proposal.total_amount.toLocaleString()}
                      </div>
                      <div className="mt-2 flex items-center space-x-2">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                          <Phone className="h-3.5 w-3.5" />
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                          <MessageSquare className="h-3.5 w-3.5" />
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden sm:block">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading proposals...</p>
              </div>
            </div>
          ) : filteredProposals.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <EmptyState
                icon={<FileText className="w-8 h-8 text-gray-400" />}
                title={searchQuery ? 'No matching proposals' : 'No proposals yet'}
                description={
                  searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start creating proposals to send to your customers.'
                }
                actionLabel={
                  searchQuery ? 'Clear search' : 'Create Proposal'
                }
                onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateModalOpen(true))}
              />
            </div>
          ) : (
            <table className="min-w-full divide-y divide-[#e1e3e5]">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#e1e3e5]">
                {filteredProposals.map((proposal) => (
                  <tr
                    key={proposal.id}
                    className="hover:bg-[#f6f6f7] transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedProposal(proposal);
                      setIsDetailsModalOpen(true);
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-black flex items-center justify-center text-xs font-medium text-white">
                          {proposal.customer?.first_name?.[0]}{proposal.customer?.last_name?.[0]}
                        </div>
                        <div className="ml-3">
                          <div className="text-xs font-medium text-gray-900">
                            {proposal.customer?.first_name} {proposal.customer?.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {proposal.customer?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                      {proposal.title}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full ${getStatusColor(
                          proposal.status
                        )}`}
                      >
                        {proposal.status.charAt(0).toUpperCase() +
                          proposal.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                      ${proposal.total_amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                          <Phone className="h-3.5 w-3.5" />
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                          <MessageSquare className="h-3.5 w-3.5" />
                        </button>
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Load More Button */}
        {!loading && hasMore && filteredProposals.length > 0 && (
          <div className="border-t border-[#e1e3e5] p-4 flex justify-center">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
      </div>

      <CreateProposalModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(createdProposal) => {
          setIsCreateModalOpen(false);
          refetch();
          
          // Show the details modal for the newly created proposal
          setSelectedProposal(createdProposal);
          setIsDetailsModalOpen(true);
        }}
      />

      <ProposalDetailsModal
        isOpen={isDetailsModalOpen && selectedProposal !== null}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedProposal(null);
        }}
        proposal={selectedProposal!}
        onUpdate={refetch}
        readOnly={readOnly}
        onDelete={refetch}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProposalToDelete(null);
        }}
        onConfirm={async () => {
          if (!proposalToDelete) return;
          try {
            setDeleteLoading(true);
            await deleteProposal(proposalToDelete.id);
            await refetch();
            setIsDeleteModalOpen(false);
            setProposalToDelete(null);
          } catch (err) {
            console.error('Error deleting proposal:', err);
            // Error state is now managed by the hook
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Proposal"
        message={`Are you sure you want to delete this proposal? This action cannot be undone.`}
        loading={deleteLoading}
      />
    </div>
  );
}

// Wrap with PermissionGuard
function ProposalsWithPermission({ readOnly = false }: ProposalsProps) {
  return (
    <PermissionGuard permission="proposals.pageAccess">
      <Proposals readOnly={readOnly} />
    </PermissionGuard>
  );
}

export default ProposalsWithPermission;